import { useEffect, useState } from "react";
import { ShippingAddress } from "../../types";

import { styled, useMediaQuery } from "@mui/material";

import { getFullAddressWithNames } from "@/utils/StringUtils";
import { AddCircleOutlineIcon } from "@brandclub/common-ui";
import { useAppSelector } from "../../../../redux/hooks";
import {
  MOBILE_SCREEN_SIZE,
  TABLET_SCREEN_SIZE,
} from "../../../AppNavigation/constants";
import StoreRadio from "../../../StoreComponents/StoreRadio";
import AddressForm from "../CheckoutV2/CheckoutForm/components/AddressForm";
import ButtonContainer from "./components/Buttons/ButtonContainer";
import SaveButton from "./components/Buttons/SaveButton";
import ClearButton from "./components/Buttons/ClearButton";
import { Address } from "@/redux/types";

const Container = styled("div")(({ theme }) => ({
  marginBottom: 20,
  ".existing": {
    display: "flex",
    flexDirection: "column",
    gap: 16,
    [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
      gap: 6,
    },
    ".info_row": {
      cursor: "pointer",
      display: "flex",
      alignItems: "flex-start",
      fontSize: 14,
      fontWeight: 500,
      ".icon": {
        width: 30,
        height: 30,
        objectFit: "contain",
        marginRight: 10,
        [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
          width: 20,
          height: 20,

          marginRight: 8,
        },
        [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
          width: 20,
          height: 20,

          marginRight: 8,
        },
      },
      ".add_new_icon": {
        lineHeight: 0,
        marginRight: 10,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        fontSize: 22,
        [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
          fontSize: 17,
        },
        [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
          fontSize: 17,
        },
      },
    },
  },

  ".actions": {
    marginTop: 30,
    display: "flex",
    gap: 20,
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
  },
}));

interface CheckoutEditShippingFormProps {
  availableAddresses?: ShippingAddress[];
  existingAddress?: ShippingAddress;
  handleSaveShippingAddress: (address: Partial<ShippingAddress>) => void;
  onChange?: (address: Partial<ShippingAddress>) => void;
}

export const isAddressEqual = (
  address1?: Address | ShippingAddress,
  address2?: Address | ShippingAddress
) => {
  return (
    (address1?.id && address2?.id && address1.id === address2.id) ||
    (address1?.address1 === address2?.address1 &&
      address1?.address2 === address2?.address2 &&
      address1?.city === address2?.city &&
      address1?.state === address2?.state &&
      address1?.zipCode === address2?.zipCode &&
      address1?.firstName === address2?.firstName &&
      address1?.lastName === address2?.lastName)
  );
};

const CheckoutEditShippingForm = ({
  existingAddress,
  handleSaveShippingAddress,
  availableAddresses,
  onChange,
}: CheckoutEditShippingFormProps) => {
  const addresses = availableAddresses;
  const phoneNumberFromProfile = useAppSelector(
    (state) => state.userProfile?.phoneNumber
  );
  const mobile = useMediaQuery(`(max-width: ${MOBILE_SCREEN_SIZE}px)`);
  const [address1, setAddress1] = useState(existingAddress?.address1 || "");
  const [address2, setAddress2] = useState(existingAddress?.address2 || "");
  const [city, setCity] = useState(existingAddress?.city || "");
  const [state, setState] = useState(existingAddress?.state || "");
  const [zipCode, setZipCode] = useState(existingAddress?.zipCode || "");
  const [firstName, setFirstName] = useState(existingAddress?.firstName || "");
  const [lastName, setLastName] = useState(existingAddress?.lastName || "");

  const [addNewOpen, setAddNewOpen] = useState(false);

  const [selectedAddressIndex, setSelectedAddressIndex] = useState<number>(-1);

  useEffect(() => {
    if (
      existingAddress?.extendedAttributes?.isSelected &&
      selectedAddressIndex === -1
    ) {
      const indexOfMatchingAddress =
        addresses?.findIndex((address) =>
          isAddressEqual(address, existingAddress)
        ) ?? -1;
      if (indexOfMatchingAddress !== -1) {
        setSelectedAddressIndex(indexOfMatchingAddress);
      }
    }
  }, [addresses, existingAddress, selectedAddressIndex]);

  const selectedAddress = addresses?.[selectedAddressIndex];

  useEffect(() => {
    if (!addresses?.length) {
      setAddNewOpen(true);
    }
  }, [addresses?.length]);

  const clearInput = () => {
    setAddress1("");
    setAddress2("");
    setCity("");
    setState("");
    setZipCode("");
    setFirstName("");
    setLastName("");
  };

  useEffect(() => {
    if (onChange) {
      if (selectedAddressIndex >= 0) {
        const address = addresses?.[selectedAddressIndex];
        if (address) {
          onChange(address);
        }
      } else {
        onChange({
          address1,
          address2,
          city,
          state,
          zipCode,
          firstName,
          lastName,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    address1,
    address2,
    city,
    state,
    zipCode,
    firstName,
    lastName,
    selectedAddressIndex,
  ]);

  const canSubmit =
    selectedAddressIndex >= 0 ||
    (address1 && city && state && zipCode && firstName && lastName);

  return (
    <Container>
      {addresses?.length ? (
        <div className="existing">
          {addresses.map((address, index) => {
            const addressDisplay = getFullAddressWithNames(address);
            const selected =
              selectedAddressIndex >= 0 && selectedAddressIndex === index;

            return (
              <div
                className="info_row"
                key={address.id}
                onClick={() => {
                  console.log("clicked", index, address);
                  setSelectedAddressIndex(index);
                  setAddNewOpen(false);
                }}
                style={{ fontWeight: selected ? 600 : 500 }}
              >
                <StoreRadio
                  key={address.id}
                  style={{ marginRight: 10 }}
                  checked={!!selected}
                  disableRipple
                  onClick={() => {}}
                />
                {addressDisplay}
              </div>
            );
          })}
          {addNewOpen ? null : (
            <div
              className="info_row"
              onClick={() => {
                setSelectedAddressIndex(-1);
                setAddNewOpen(true);
              }}
            >
              <AddCircleOutlineIcon className="add_new_icon" />
              Add new shipping address
            </div>
          )}
        </div>
      ) : null}

      {addNewOpen ? (
        <AddressForm
          firstName={firstName}
          setFirstName={setFirstName}
          lastName={lastName}
          setLastName={setLastName}
          address1={address1}
          setAddress1={setAddress1}
          address2={address2}
          setAddress2={setAddress2}
          city={city}
          setCity={setCity}
          state={state}
          setState={setState}
          zipCode={zipCode}
          setZipCode={setZipCode}
        />
      ) : null}

      <ButtonContainer
        sx={{
          marginTop: "35px",
          [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
            marginTop: "20px",
          },
          alignSelf: "flex-end",
          justifyContent: "space-between",
        }}
      >
        {mobile ? null : <ClearButton onClick={clearInput}>Clear</ClearButton>}
        <SaveButton
          disabled={!canSubmit}
          onClick={() => {
            if (selectedAddress) {
              handleSaveShippingAddress({
                address1: selectedAddress.address1,
                address2: selectedAddress.address2,
                city: selectedAddress.city,
                state: selectedAddress.state,
                zipCode: selectedAddress.zipCode,
                firstName: selectedAddress.firstName,
                lastName: selectedAddress.lastName,
                phoneNumber: phoneNumberFromProfile as string,
              });
            } else {
              handleSaveShippingAddress({
                address1,
                address2,
                city,
                state,
                zipCode,
                firstName,
                lastName,
                phoneNumber: phoneNumberFromProfile as string,
              });
            }
          }}
        >
          Save
        </SaveButton>
      </ButtonContainer>
    </Container>
  );
};

export default CheckoutEditShippingForm;
